import React, { useState, useEffect } from "react";
import "../../../css/menu.css";
import { useTranslation } from "react-i18next";
import { Button } from "@material-ui/core";
import { DiscountsListFilterIcon } from "../../discounts/ui/DiscountsFilterButton"
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";

interface Filter {
  key: string;
  label: string;
  param: string;
  expected: any;
  unique?: string;
}

export interface FilterSection {
  key: string;
  label: string;
  filters: Filter[];
}

const iFilters = {
  inStock: false,
  outStock: false,
  table: false,
  delivery: false,
  collection: false,
};

const menuItemFilters: FilterSection[] = [
  {
    key: "stock",
    label: "filters.stock",
    filters: [
      {
        key: "inStock",
        label: "filters.inStock",
        param: "enabled",
        expected: true,
      },
      {
        key: "outStock",
        label: "filters.outStock",
        param: "enabled",
        expected: false,
      },
    ],
  },
];

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}
export interface IFilterState {
  allFilters: FilterSection[],
  filters: any;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          data-component-name="CLOSE_BUTTON"
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    minWidth: "320px",
    borderBottom: "none",
  },
}))(MuiDialogContent);

interface FiltersProps {
  setFilterState: (allFilters: FilterSection[], filterState: any) => void;
  allFilters?: FilterSection[];
  filterkey?: string;
  defaultFilters?: Record<string, boolean>;
  filterTitle?: string;
}

const Filters = ({ setFilterState, allFilters = menuItemFilters, filterTitle, filterkey = "filters", defaultFilters = iFilters }: FiltersProps) => {
  const { t } = useTranslation();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<Record<string, boolean> | null>(
    null
  );
  // console.log(allFilters);
  useEffect(() => {
    if (filterkey) {
      if (localStorage.getItem(filterkey)) {
        const newState = santiseLocalState(filterkey, allFilters);
        setFilters(newState);
        return;
      }
    }
    setFilters(defaultFilters);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (filters === null) return;

    if (filterkey) {
      localStorage.setItem(filterkey, JSON.stringify(filters));
    }
    setFilterState(allFilters, filters);
    //eslint-disable-next-line
  }, [filters]);

  const renderFilter = (item: Filter) => {
    return <FormControlLabel
      style={{ marginLeft: "1rem" }}
      key={item.key}
      control={
        <Checkbox
          checked={item.key in filters! ? filters![item.key] : false}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFilters({
              ...filters,
              [event.target.name]: event.target.checked,
            });
          }}
          name={t(item.key)}
        />
      }
      label={t(item.label)}
    />
  };

  const renderClearFiltersButton = () => {
    return (
      <Button
        onClick={() => {
          setFilters({});
        }}
        data-component-name="CLEAR_BUTTON"
      >
        {t("filters.clear")}
      </Button>
    );
  };

  const renderFilterCount = () =>
    (filters && Object.values(filters).filter((filter) => filter).length) || 0;

  const renderFilters = (filters: Filter[]) => (
    <FormGroup style={{ margin: "0.5rem 0" }}>
      {filters.map((filter: Filter) => renderFilter(filter))}
    </FormGroup>
  );

  const renderFilterSections = () =>

    allFilters.map((section: FilterSection) => {
      return (
        <Typography variant="body1" key={section.key}>
          <Typography variant="h6">{t(section.label)}</Typography>
          {section?.filters && Array.isArray(section?.filters) && renderFilters(section.filters)}
        </Typography>
      );
    });

  return (
    <>
      <DiscountsListFilterIcon title={t(filterTitle || "filters.filters")} onClick={() => {
        setFiltersOpen(true);
      }} selectedFilterCount={renderFilterCount()} />
      <Dialog
        onClose={() => {
          setFiltersOpen(false);
        }}
        aria-labelledby="customized-dialog-title"
        open={filtersOpen}
      >
        <DialogTitle
          id="customized-dialog-title"
          onClose={() => {
            setFiltersOpen(false);
          }}
        >
          {t("filters.filters")} {renderClearFiltersButton()}
        </DialogTitle>

        <DialogContent dividers>{filters !== null && renderFilterSections()}</DialogContent>
      </Dialog>
    </>
  );
};

export function sortFilterItems(allFilters: FilterSection[], filters: any, listItems: any[]) {
  let list = listItems;
  if (listItems.length === 0) {
    return list;
  }
  Object.values(allFilters).forEach((section: FilterSection) => {
    const all = section.filters.every(
      (filter: Filter) => filters[filter.key]
    );
    const neither = section.filters.every(
      (filter: Filter) => !filters[filter.key]
    );

    if (!(all || neither)) {
      /** From 'list', only return those items which exist in 'filters' and match expectation */
      list = list.filter((item) => {
        return section.filters.filter(
          (filter: Filter) =>
            filters[filter.key] && item[filter.param] === filter.expected
        ).length;
      });
    }
  });
  return list;
}

/**
 * Santises the local state 
 * If the filter key no longer exists in the filter all state
 * then we remove it from the object assuming it has been deleted/removed
*/
function santiseLocalState(filterKey: string, filters: FilterSection[]) {
  const filtersState = JSON.parse(localStorage.getItem(filterKey) as string);
  const newObj: Record<string, boolean> = {};
  for (const key in filtersState) {
    let isAvailable = false;
    for (const item of filters) {
      for (const filter of item.filters) {
        if (filter.key === key) {
          isAvailable = true;
          break;
        }
      }
    }
    if (isAvailable) {
      newObj[key] = filtersState[key];
    }
  }
  return newObj;
}

export default Filters;
